import React from 'react';
import { motion } from 'framer-motion';
import { RotateCcwIcon } from 'lucide-react';
import { Diagnosis } from '../utils/diagnosis';
import { ScoreRing } from './ScoreRing';
import { Closing } from './Closing';
import { Footer } from './Footer';
type ResultProps = {
  diagnosis: Diagnosis;
  onRestart: () => void;
};
const card = (index: number) => ({
  initial: {
    opacity: 0,
    y: 24
  },
  animate: {
    opacity: 1,
    y: 0
  },
  transition: {
    duration: 0.9,
    delay: 0.7 + index * 0.18,
    ease: [0.22, 1, 0.36, 1] as const
  }
});
export function Result({
  diagnosis,
  onRestart
}: ResultProps) {
  return <div className="w-full bg-beige-50" data-id="element-86" data-mp="1zvyqffbtisj9">
      <section className="mx-auto w-full max-w-2xl px-6 pb-24 pt-20 sm:px-10 sm:pt-28" data-id="element-87" data-mp="1zx0ag9atapd4">
        <motion.p initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.9,
        ease: [0.22, 1, 0.36, 1]
      }} className="text-center text-[0.7rem] uppercase tracking-[0.32em] text-sage-600" data-id="element-88" data-mp="23xajxvxtbxwq">
          Tu Skin Fresh Index™
        </motion.p>

        <div className="mt-12" data-id="element-89" data-mp="3l1ry8xqcy83g">
          <ScoreRing score={diagnosis.score} data-id="element-90" data-mp="3g49z1z25778n" />
        </div>

        <motion.h1 initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.9,
        delay: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }} className="mx-auto mt-12 max-w-lg text-center font-serif text-3xl font-light leading-[1.15] text-warmgray-800 sm:text-[2.5rem]" data-id="element-91" data-mp="3g6w51u9dxeor">
          {diagnosis.headline}
        </motion.h1>

        <div className="mt-16 grid grid-cols-1 gap-5" data-id="element-92" data-mp="3nwk8dbi4m3v">
          {/* 1 — Fortalezas */}
          <motion.article {...card(0)} className="rounded-4xl border border-beige-300 bg-white px-7 py-9 sm:px-10 sm:py-11" data-id="element-93" data-mp="3v0r82jmv09s1">
            <h2 className="font-serif text-2xl font-light text-warmgray-800" data-id="element-94" data-mp="rcpbhwh6i5s5">
              Tu piel está funcionando bien en
            </h2>
            <ul className="mt-7 flex flex-col gap-4" data-id="element-95" data-mp="mko0seznsn48">
              {diagnosis.strengths.map(item => <li key={item.key} className="flex items-center gap-4" data-id="element-96" data-mp="15j0wy6jmgmfj">
                  <span aria-hidden="true" className="text-sage-600" data-id="element-97" data-mp="1agixp9nhjvcg">
                    ✔
                  </span>
                  <span className="flex-1 text-base font-light text-warmgray-800" data-id="element-98" data-mp="wpnyyq6pauml">
                    {item.label}
                  </span>
                  <span className="text-xs font-light tabular-nums text-warmgray-400" data-id="element-99" data-mp="mrzq6wc9fmlf">
                    {item.score}
                  </span>
                </li>)}
            </ul>
          </motion.article>

          {/* 2 — Mejoras */}
          <motion.article {...card(1)} className="rounded-4xl border border-sage-200 bg-sage-50 px-7 py-9 sm:px-10 sm:py-11" data-id="element-100" data-mp="dsmoipudo2b9">
            <h2 className="font-serif text-2xl font-light text-warmgray-800" data-id="element-101" data-mp="284t37ymhd18b">
              Mejoras recomendadas
            </h2>
            <ul className="mt-7 flex flex-col gap-5" data-id="element-102" data-mp="2c5mrit3u4i1q">
              {diagnosis.improvements.map(item => <li key={item.key} data-id="element-103" data-mp="k41h1e4mw55">
                  <div className="flex items-baseline justify-between" data-id="element-104" data-mp="3snddji4rb4ha">
                    <span className="text-base font-light text-warmgray-800" data-id="element-105" data-mp="3sp7ygwg12wld">
                      {item.label}
                    </span>
                    <span className="text-xs font-light tabular-nums text-warmgray-400" data-id="element-106" data-mp="5ixgwzcz4x7y">
                      {item.score}
                    </span>
                  </div>
                  <div className="mt-2 h-px w-full bg-sage-200" data-id="element-107" data-mp="k9ehkghsbj3s">
                    <motion.div className="h-px bg-sage-500" initial={{
                  width: 0
                }} animate={{
                  width: `${item.score}%`
                }} transition={{
                  duration: 1.4,
                  delay: 1.2,
                  ease: [0.22, 1, 0.36, 1]
                }} data-id="element-108" data-mp="f7p7p46atbha" />
                  </div>
                </li>)}
            </ul>
          </motion.article>

          {/* 3 — Enfoque */}
          <motion.article {...card(2)} className="rounded-4xl border border-beige-300 bg-white px-7 py-9 sm:px-10 sm:py-11" data-id="element-109" data-mp="2sh9nz46guskk">
            <h2 className="font-serif text-2xl font-light text-warmgray-800" data-id="element-110" data-mp="31h6fddk8q5jb">
              Tu piel necesita enfocarse en
            </h2>
            <ul className="mt-7 flex flex-col divide-y divide-beige-200 border-y border-beige-200" data-id="element-111" data-mp="36clqvyurzpck">
              {diagnosis.focus.map(item => <li key={item} className="flex items-start gap-4 py-4" data-id="element-112" data-mp="2drc3ay7qwl04">
                  <span aria-hidden="true" className="mt-0.5 text-sage-600" data-id="element-113" data-mp="2ipnbl4ie6ng8">
                    ✦
                  </span>
                  <span className="text-base font-light leading-relaxed text-warmgray-800" data-id="element-114" data-mp="2nh5evz7w104d">
                    {item}
                  </span>
                </li>)}
            </ul>
          </motion.article>

          {/* 4 — Lectura */}
          <motion.article {...card(3)} className="overflow-hidden rounded-4xl border border-beige-300 bg-white" data-id="element-115" data-mp="1uxgbpvdgjyc2">
            <img src="https://cdn.magicpatterns.com/patterns/generated-images/44db1689-4609-4c70-a3dc-888b75c3ad6e.jpg" alt="" aria-hidden="true" className="h-32 w-full object-cover sm:h-40" data-id="element-116" data-mp="1ii8jsv6b1bui" />
            <div className="px-7 py-9 sm:px-10 sm:py-11" data-id="element-117" data-mp="14hfls0gsnymn">
              <h2 className="font-serif text-2xl font-light text-warmgray-800" data-id="element-118" data-mp="14iz19oumm5nd">
                Cómo leer tu resultado
              </h2>
              <p className="mt-5 text-sm font-light leading-relaxed text-warmgray-600 sm:text-base" data-id="element-119" data-mp="urmaz1oh7njg">
                {diagnosis.explanation}
              </p>
              <p className="mt-5 text-sm font-light leading-relaxed text-warmgray-600 sm:text-base" data-id="element-120" data-mp="pu2yo7bc9ku5">
                Pequeñas mejoras sostenidas en estas áreas generan una diferencia
                visible con el tiempo: no se trata de sumar productos, sino de
                ordenar los gestos correctos en el momento correcto.
              </p>
            </div>
          </motion.article>
        </div>

        <Closing diagnosis={diagnosis} data-id="element-121" data-mp="b746ub8kkoew" />

        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.9,
        ease: [0.22, 1, 0.36, 1]
      }} className="mt-20 flex flex-col items-center" data-id="element-122" data-mp="3tbpckqgku636">
          <button type="button" onClick={onRestart} className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-warmgray-400 transition-colors duration-500 hover:text-sage-700" data-id="element-123" data-mp="pvigp64eld4y">
            <RotateCcwIcon className="h-3.5 w-3.5" strokeWidth={1.25} data-id="element-124" data-mp="l3gyz4oguu7j" />
            Repetir el diagnóstico
          </button>
        </motion.div>
      </section>

      <Footer data-id="element-125" data-mp="18ri4e4qcluiq" />
    </div>;
}